import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import config from "../../config/api";
import axiosInstance from "../../config/axiosConfig";
import { setRealtimeEvent } from "../../realtime/realtimeSlice";
import {
  addItemToList,
  getRealtimeEntityFromPayload,
  isModuleMatch,
  removeItemFromList,
  updateItemInList,
} from "../../realtime/realtimeUtils";

const API_URL = config.backendUrl + "/hardware-description";

export const fetchHardwareDescriptions = createAsyncThunk(
  "hardwareDescription/fetchAll",
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`${API_URL}/`);
      return response.data.data; // assuming data is inside response.data.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchHardwareProjectTracking = createAsyncThunk(
  "hardwareDescription/fetchHardwareProjectTracking",
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`${API_URL}/getall-project-track`);
      return response.data.data; // assuming data is inside response.data.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveHardwareDescriptions = createAsyncThunk(
  "hardwareDescription/saveBatch",
  async (payload, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`${API_URL}/save`, {
        updates: payload, // expects array of objects
      });
      return response.data.data; // array of new/updated items
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const replaceAllHardwareDescriptions = createAsyncThunk(
  "hardwareDescription/replaceAll",
  async (payload, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`${API_URL}/replace`, payload);
      return response.data.message; // success message
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteHardwareDescription = createAsyncThunk(
  "hardwareDescription/delete",
  async (id, thunkAPI) => {
    try {
      await axiosInstance.delete(`${API_URL}/delete/${id}`);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const hardwareDescriptionSlice = createSlice({
  name: "hardwareDescription",
  initialState: {
    items: [],
    hardwareProjectTracking: [],
    loading: false,
    error: null,
    replaceMessage: "",
  },
  reducers: {
    clearHardwareError: (state) => {
      state.error = null;
    },
    clearReplaceMessage: (state) => {
      state.replaceMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder

    .addCase(fetchHardwareDescriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHardwareDescriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchHardwareDescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchHardwareProjectTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHardwareProjectTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.hardwareProjectTracking = action.payload;
      })
      .addCase(fetchHardwareProjectTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(saveHardwareDescriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveHardwareDescriptions.fulfilled, (state, action) => {
        state.loading = false;
        const updatedItems = action.payload;
        updatedItems.forEach((newItem) => {
          const index = state.items.findIndex((i) => i._id === newItem._id);
          if (index >= 0) {
            state.items[index] = newItem;
          } else {
            state.items.push(newItem);
          }
        });
      })
      .addCase(saveHardwareDescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(replaceAllHardwareDescriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(replaceAllHardwareDescriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.replaceMessage = action.payload;
      })
      .addCase(replaceAllHardwareDescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteHardwareDescription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteHardwareDescription.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((item) => item._id !== action.payload);
      })
      .addCase(deleteHardwareDescription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(setRealtimeEvent, (state, action) => {
        const { module, action: eventAction, data } = action.payload || {};
        if (!isModuleMatch(module, ["hardware-description"])) return;

        const incomingItem = getRealtimeEntityFromPayload(data);
        if (!incomingItem) return;

        const applyListUpdate = (listKey) => {
          if (eventAction === "updated") {
            state[listKey] = updateItemInList(state[listKey], incomingItem);
          }
          if (eventAction === "created") {
            state[listKey] = addItemToList(state[listKey], incomingItem);
          }
          if (eventAction === "deleted") {
            state[listKey] = removeItemFromList(state[listKey], incomingItem);
          }
        };

        applyListUpdate("items");
        applyListUpdate("hardwareProjectTracking");
      });
  },
});

export const { clearHardwareError, clearReplaceMessage } =
  hardwareDescriptionSlice.actions;

export default hardwareDescriptionSlice.reducer;
